import { useState } from "react";

import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { EntityActionParams } from "../../../lib/hooks/api/leads/useLeadEntityActions";
import { Flex, Text } from "../../../styles/utility-components";
import DocumentDrawer from "../../document_details/DocumentDrawer";
import { DEFAULT_DOCUMENT_FILTERS, getDocumentTypeLabel } from "../../documents/utils";
import OrganisationsPopover from "../../record_details/OrganisationsPopover";
import DisplayDate from "../../ui/date/DisplayDate";
import EntityCardContent from "../EntityCardContent";
import EntityProperty from "../EntityProperty";
import FindEntity from "../FindEntity";
import { SavedEntityCardContainer } from "../utils";

// Don't want any of these types to be optional because we want to ensure future object types remember to include these values
type DocumentEntity = {
  id: string;
  title: string;
  publishedAt: string | null;
  category: string;
  buyers: { id: string; name: string }[];
};

export default function SaveDocumentEntitiesCard({
  documents,
  entityUrl,
  removeEntity,
}: {
  documents: DocumentEntity[];
  entityUrl: string;
  removeEntity: (entity: EntityActionParams) => void;
}) {
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>();

  const onOpenChange = (open: boolean) => {
    if (!open) {
      setSelectedDocumentId(undefined);
    } else {
      console.warn("onOpenChange with open=true is unsupported in SaveDocumentEntitiesCard");
    }
  };

  return (
    <Flex column gap={16}>
      {documents.map((document) => (
        <DocumentCard
          key={document.id}
          document={document}
          onView={() => setSelectedDocumentId(document.id)}
          removeEntity={() =>
            removeEntity({
              entityType: "document",
              entityId: document.id,
            })
          }
        />
      ))}
      <FindEntity entity="document" url={entityUrl} isEmptyState={!documents.length} />
      <DocumentDrawer
        open={!!selectedDocumentId}
        onOpenChange={onOpenChange}
        documentId={selectedDocumentId ?? undefined}
        filters={DEFAULT_DOCUMENT_FILTERS}
        disablePermissionCheck
      />
    </Flex>
  );
}

function DocumentCard({
  document,
  onView,
  removeEntity,
}: {
  document: DocumentEntity;
  onView: () => void;
  removeEntity: () => void;
}) {
  const { data: currentUser } = useCurrentUser();
  return (
    <SavedEntityCardContainer
      key={document.id}
      onClick={() => {
        onView();
      }}
    >
      <EntityCardContent
        header={<Text fontWeight="600">{document.title}</Text>}
        entity="document"
        removeEntity={removeEntity}
      >
        <Flex column gap={16} width="100%">
          <EntityProperty
            title="Document type"
            value={getDocumentTypeLabel(document.category, currentUser)}
            icon="box"
          />
          <EntityProperty
            title="Buyer(s)"
            value={<OrganisationsPopover orgType="buyers" orgs={document.buyers} />}
            icon="building08"
          />
          <EntityProperty
            title="Publish date"
            value={
              document.publishedAt ? <DisplayDate value={new Date(document.publishedAt)} /> : null
            }
            icon="calendar"
          />
        </Flex>
      </EntityCardContent>
    </SavedEntityCardContainer>
  );
}
